import * as React from "react";
import { useStyletron } from "baseui";
import { Button } from "baseui/button";
import { Card } from "baseui/card";
import { SIZE } from "baseui/input";
import { ALIGNMENT, Cell, Grid } from "baseui/layout-grid";
import { OnChangeParams, Select, Value } from "baseui/select";
import { DatePicker } from "baseui/datepicker";
import { FormControl } from "baseui/form-control";
import { HeadingActionsBar } from "kanopi-components/HeadingActionBar/HeadingActionsBar";
import { toaster } from "kanopi-components/ToastContainer";
import { useAPI } from "kw/api/useAPI";
import { useFormatter } from "kw/util/useFormatter";
import { useTaskPoll } from "kanopi-components/useTaskPoll";
import api from "kw/api";

type TSearchFilters = {
  label: string;
  value: string;
};

export type TTableAction = {
  searchFilters: TSearchFilters[];
  title: string;
  onReportGenerated: () => void;
};

export const ReportsAction = (props: TTableAction) => {
  const { searchFilters, title, onReportGenerated } = props;
  const [, theme] = useStyletron();
  const filterPlaceholder = "Select a report";
  const { mutation, loading } = useAPI();
  const { formatDate } = useFormatter();

  const [reportType, setReportType] = React.useState<Value>();
  const [startDate, setStartDate] = React.useState("");
  const [endDate, setEndDate] = React.useState("");
  const reportIdRef = React.useRef<string | null>(null);

  const isFormComplete = reportType?.length && startDate && endDate;

  const addDays = (date: Date, days: number) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
  };
  const MAX_DAYS = 366; // Allow for a leap year.

  const { loading: polling, taskPoll } = useTaskPoll({
    poll: async () => {
      if (!reportIdRef.current) return "unknown";

      const response = await api.query({
        admin: {
          report: [
            { id: reportIdRef.current },
            {
              taskStatus: true,
            },
          ],
        },
      });
      const status = response?.admin?.report?.taskStatus || "unknown";
      return status;
    },
    query: async () => {
      return "success";
    },
  });

  React.useEffect(() => {
    if (!polling && reportIdRef.current) {
      onReportGenerated();

      toaster({
        kind: "POSITIVE",
        content: `Report ID ${reportIdRef.current} generation completed.`,
        key: "generate-report-complete-toast",
      });

      // Reset reportId
      reportIdRef.current = null;
    }
  }, [polling]);

  const handleGenerate = () => {
    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);
    const reportTypeValue = reportType?.length ? reportType[0].value : "";
    const reportNameValue = reportType?.length ? reportType[0].label : "";
    mutation(
      {
        admin: {
          generateReport: [
            {
              input: {
                reportType: reportTypeValue,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                reportName: reportNameValue,
              },
            },
            {
              id: true,
            },
          ],
        },
      },
      (response: { admin: { generateReport: { id: string } } }) => {
        const reportId = response.admin.generateReport.id;
        reportIdRef.current = reportId;

        taskPoll();

        toaster({
          kind: "INFO",
          content: `Report ID ${reportId} generation started.`,
          key: "generate-report-started-toast",
        });
      }
    );
  };

  return (
    <Card
      overrides={{
        Root: {
          style: {
            width: "100%",
            borderLeftWidth: 0,
            borderRightWidth: 0,
            borderTopWidth: 0,
            borderBottomWidth: 0,
            boxShadow: theme.lighting.shadow400,
            borderTopLeftRadius: theme.borders.radius300,
            borderTopRightRadius: theme.borders.radius300,
            borderBottomLeftRadius: theme.borders.radius300,
            borderBottomRightRadius: theme.borders.radius300,
          },
        },
      }}
    >
      <HeadingActionsBar heading={title} headingLevel="section" />
      <Grid
        gridColumns={12}
        gridMargins={0}
        gridMaxWidth="100%"
        overrides={{
          Grid: {
            style: {
              paddingTop: theme.sizing.scale600,
              paddingBottom: theme.sizing.scale600,
            },
          },
        }}
      >
        <Cell span={[12, 12, 4]}>
          <FormControl
            label="Report Type"
            overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}
          >
            <Select
              size={SIZE.compact}
              options={searchFilters}
              value={reportType}
              clearable={false}
              placeholder={filterPlaceholder}
              labelKey="label"
              valueKey="label"
              onChange={(params: OnChangeParams) => {
                setReportType(params.value);
              }}
            />
          </FormControl>
        </Cell>
        <Cell span={[12, 12, 2]}>
          <FormControl
            label="Start date"
            overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}
          >
            <DatePicker
              size={SIZE.compact}
              value={startDate}
              placeholder="DD/MM/YYYY"
              onChange={({ date }) => {
                setStartDate(date);
              }}
              formatString="dd/MM/yyyy"
            />
          </FormControl>
        </Cell>
        <Cell span={[12, 12, 2]}>
          <FormControl
            label="End date (exclusive)"
            overrides={{ ControlContainer: { style: { marginBottom: 0 } } }}
          >
            <DatePicker
              size={SIZE.compact}
              value={endDate}
              placeholder="DD/MM/YYYY"
              onChange={({ date }) => date && setEndDate(date)}
              formatString="dd/MM/yyyy"
              minDate={startDate ? addDays(startDate, 1) : null} // At least 1 day later
              maxDate={startDate ? addDays(startDate, MAX_DAYS) : null}
            />
          </FormControl>
        </Cell>
        <Cell span={[12, 12, 2]} />
        <Cell span={[12, 12, 2]} align={ALIGNMENT.end}>
          <Button
            size={SIZE.compact}
            onClick={handleGenerate}
            overrides={{
              BaseButton: {
                style: {
                  maxWidth: "70%",
                  marginTop: theme.sizing.scale800,
                },
              },
            }}
            disabled={!isFormComplete || loading || polling}
          >
            {loading || polling ? "Generating..." : "Generate"}
          </Button>
        </Cell>
      </Grid>
    </Card>
  );
};
